export const privacy = {
  // SEO
  title: 'Privacy Policy - KiraCar',
  description: 'Learn how KiraCar protects your privacy and handles your personal data. Our comprehensive privacy policy covers data collection, usage, and your rights.',
  keywords: 'privacy policy, data protection, GDPR, personal data, KiraCar, car rental, privacy rights',
  
  // Hero Section
  heroTitle: 'Privacy Policy',
  heroDescription: 'Your privacy is important to us. This policy explains how we collect, use, and protect your information.',
  lastUpdated: 'Last updated',
  
  // Sections - Using same structure as French
  introduction: {
    title: 'Introduction',
    paragraph1: 'KiraCar ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our car rental platform and services.',
    paragraph2: 'By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.'
  },
  
  informationWeCollect: {
    title: 'Information We Collect',
    personalInformation: {
      title: 'Personal Information',
      accountInfo: { title: 'Account Information', description: 'Name, email address, phone number, date of birth, profile picture' },
      identityDocs: { title: 'Identity Documents', description: 'Driver\'s license, passport, identity card, residence permit' },
      locationData: { title: 'Location Data', description: 'Pickup and return locations, GPS coordinates, address information' }
    },
    bookingInformation: {
      title: 'Booking Information',
      rentalDetails: { title: 'Rental Details', description: 'Pickup and return dates, vehicle preferences, rental duration, pricing information' },
      paymentInfo: { title: 'Payment Information', description: 'Payment method details, transaction history, billing address (processed securely through third-party providers)' }
    },
    technicalInformation: {
      title: 'Technical Information',
      usageData: { title: 'Usage Data', description: 'IP address, browser type, device information, operating system, pages visited, time spent on site' },
      cookies: { title: 'Cookies and Tracking', description: 'Session cookies, preference cookies, analytics cookies, marketing cookies' }
    }
  },
  
  howWeUse: {
    title: 'How We Use Your Information',
    serviceDelivery: { badge: 'Service Delivery', title: 'Provide Our Services', description: 'Process bookings, manage rentals, handle payments, provide customer support' },
    communication: { badge: 'Communication', title: 'Communication', description: 'Send booking confirmations, rental reminders, service updates, marketing communications (with consent)' },
    security: { badge: 'Security', title: 'Security and Fraud Prevention', description: 'Verify identity, prevent fraud, ensure platform security, comply with legal requirements' },
    improvement: { badge: 'Improvement', title: 'Service Improvement', description: 'Analyze usage patterns, improve user experience, develop new features, conduct research' },
    legal: { badge: 'Legal', title: 'Legal Compliance', description: 'Comply with applicable laws, respond to legal requests, protect our rights and interests' }
  },
  
  informationSharing: {
    title: 'Information Sharing',
    intro: 'We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:',
    serviceProviders: { badge: 'Service Providers', title: 'Trusted Service Providers', description: 'Payment processors, email services, analytics providers, cloud storage providers (under strict data protection agreements)' },
    businessPartners: { badge: 'Business Partners', title: 'Business Partners', description: 'Car rental agencies, insurance providers, maintenance services (only information necessary for service delivery)' },
    legalRequirements: { badge: 'Legal Requirements', title: 'Legal Requirements', description: 'When required by law, court order, or government request' },
    safety: { badge: 'Safety', title: 'Safety and Security', description: 'To protect our users, prevent fraud, or ensure platform security' }
  },
  
  dataSecurity: {
    title: 'Data Security',
    intro: 'We implement appropriate technical and organizational measures to protect your personal information:',
    encryption: { title: 'Encryption', description: 'All data is encrypted in transit and at rest using industry-standard encryption protocols' },
    accessControls: { title: 'Access Controls', description: 'Strict access controls and authentication mechanisms limit who can access your data' },
    regularAudits: { title: 'Regular Audits', description: 'Regular security audits and vulnerability assessments to identify and address potential risks' },
    secureInfrastructure: { title: 'Secure Infrastructure', description: 'Hosted on secure, compliant cloud infrastructure with regular backups and monitoring' }
  },
  
  yourRights: {
    title: 'Your Privacy Rights',
    intro: 'You have the following rights regarding your personal information:',
    access: { badge: 'Access', title: 'Right to Access', description: 'Request a copy of the personal information we hold about you' },
    correction: { badge: 'Correction', title: 'Right to Correction', description: 'Request correction of inaccurate or incomplete personal information' },
    deletion: { badge: 'Deletion', title: 'Right to Deletion', description: 'Request deletion of your personal information (subject to legal and contractual obligations)' },
    portability: { badge: 'Portability', title: 'Right to Data Portability', description: 'Request transfer of your data to another service provider' },
    objection: { badge: 'Objection', title: 'Right to Object', description: 'Object to processing of your personal information for certain purposes' },
    withdraw: { badge: 'Withdraw', title: 'Right to Withdraw Consent', description: 'Withdraw consent for processing based on consent at any time' }
  },
  
  cookies: {
    title: 'Cookies and Tracking Technologies',
    intro: 'We use cookies and similar tracking technologies to enhance your experience:',
    essential: { badge: 'Essential', title: 'Essential Cookies', description: 'Required for basic website functionality, authentication, and security' },
    analytics: { badge: 'Analytics', title: 'Analytics Cookies', description: 'Help us understand how you use our website to improve our services' },
    preferences: { badge: 'Preferences', title: 'Preference Cookies', description: 'Remember your settings and preferences for a personalized experience' },
    marketing: { badge: 'Marketing', title: 'Marketing Cookies', description: 'Used to deliver relevant advertisements and measure campaign effectiveness (with consent)' }
  },
  
  dataRetention: {
    title: 'Data Retention',
    intro: 'We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy:',
    accountData: { badge: 'Account Data', title: 'Account Information', description: 'Retained while your account is active and for 3 years after account closure for legal and business purposes' },
    bookingData: { badge: 'Booking Data', title: 'Booking Records', description: 'Retained for 7 years for accounting, legal, and dispute resolution purposes' },
    marketingData: { badge: 'Marketing Data', title: 'Marketing Communications', description: 'Retained until you unsubscribe or withdraw consent' },
    analyticsData: { badge: 'Analytics Data', title: 'Analytics Data', description: 'Anonymized and retained for up to 2 years for service improvement' }
  },
  
  internationalTransfers: {
    title: 'International Data Transfers',
    intro: 'Your personal information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place:',
    adequacy: { badge: 'Adequacy', title: 'Adequacy Decisions', description: 'Transfers to countries with adequate data protection laws' },
    sccs: { badge: 'SCCs', title: 'Standard Contractual Clauses', description: 'EU-approved contractual clauses for international transfers' },
    certification: { badge: 'Certification', title: 'Certification Schemes', description: 'Participation in recognized data protection certification schemes' }
  },
  
  childrenPrivacy: {
    title: 'Children\'s Privacy',
    intro: 'Our services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.',
    importantNotice: { title: 'Important Notice', description: 'If we discover that we have collected personal information from a child under 18, we will delete that information immediately.' }
  },
  
  contactUs: {
    title: 'Contact Us',
    intro: 'If you have any questions about this Privacy Policy or our data practices, please contact us:',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    addressLine1: 'KiraCar',
    addressLine2: 'Morocco',
    addressLine3: 'Casablanca, Morocco'
  },
  
  changesToPolicy: {
    title: 'Changes to This Policy',
    intro: 'We may update this Privacy Policy from time to time. We will notify you of any changes by:',
    notification: { badge: 'Notification', title: 'Email Notification', description: 'Sending you an email notification of significant changes' },
    website: { badge: 'Website', title: 'Website Notice', description: 'Posting the updated policy on our website with a new "Last Updated" date' },
    inApp: { badge: 'In-App', title: 'In-App Notification', description: 'Displaying a notice in our mobile app or website' },
    yourRights: { title: 'Your Rights', description: 'Continued use of our services after changes constitutes acceptance of the updated policy. You may withdraw consent at any time.' }
  },
  
  footer: {
    effectiveDate: 'This Privacy Policy is effective as of',
    lastUpdated: 'and was last updated on',
    copyright: 'All rights reserved.'
  }
};
